/**
 * OTRACK draft-lock gate (AutoCAD object-snap tracking / F11) — the impure
 * wrapper around the pure {@link resolveOtrackAlignment} geometry helper.
 * Sibling of {@link ./polarDraftLock} / {@link ./orthoDraftLock}: owns the
 * single place that reads the live OTRACK sysvar + acquired-point store and
 * hands the pointer-input seam one function.
 *
 * Gating (ALL must hold for OTRACK to engage):
 *   - `snapMagnetized` is false — a real OSNAP hit always overrides OTRACK,
 *     exactly like ORTHO/POLAR.
 *   - OTRACK is EFFECTIVE (`OTRACKMODE`, `selectEffectiveOtrack`).
 *   - ≥1 point is acquired (`useOtrackAcquisitionStore`).
 *
 * UNLIKE ortho/polar, OTRACK needs NO draft anchor — AutoCAD lets OTRACK
 * engage on the very first point of a command, since its rays come from the
 * ACQUIRED points, not `$LASTPOINT`.
 */

import {
  otrackAlignmentAngles,
  resolveOtrackAlignment,
  type OtrackPoint,
  type OtrackRayHit,
} from "./otrackConstraint";
import type { PolarTrackingConfig } from "./polarConstraint";
import {
  useSysvarStore,
  selectEffectiveOtrack,
  selectOtrackUsesPolarAngles,
  selectPolarAddAngEnabled,
  selectPolarAngleBasis,
} from "../../dynamicInput/sysvarStore";
import { usePolarTrackingPrefs } from "../../state/polarTrackingPrefs";
import { useOtrackAcquisitionStore } from "../../state/otrackAcquisitionStore";

/**
 * Hover-dwell acquisition delay (AutoCAD's own pause is short and
 * undocumented as an exact millisecond figure — this is a reasonable,
 * explicitly-documented constant, consumed by the pick-loop dwell timer in
 * `interactivePickLoop.ts`, NOT re-derived there).
 */
export const OTRACK_DWELL_MS = 350;

export interface OtrackGuideInfo {
  readonly rays: readonly OtrackRayHit[];
  /** Rendered tracking tooltip — one "distance < angle°" reading per engaged ray, joined. */
  readonly tip: string;
}

export interface OtrackDraftLock {
  readonly world: OtrackPoint;
  /** Non-null iff OTRACK geometrically ENGAGED (a ray or intersection locked). */
  readonly guide: OtrackGuideInfo | null;
}

/** Format one ray's readout, mirroring `polarDraftLock.formatPolarTip`. */
function formatOtrackRayTip(ray: OtrackRayHit): string {
  const dist = Math.abs(ray.distance).toFixed(4);
  // OTRACK-SUBSYSTEM (local Codex review round 5 P3) — `ray.point` sits
  // BEHIND the acquired point along `angleDeg` whenever `ray.distance` is
  // negative (`projectOntoRay`'s signed `distanceAlongRay`), i.e. the
  // guide/snap actually reads in the OPPOSITE (+180°) direction from the
  // acquired point. `Math.abs` alone fixes the DISTANCE reading but leaves
  // the ANGLE pointing the wrong way (e.g. "10.0000 < 0°" for a point that
  // is genuinely 10 units in the 180° direction) — flip the angle to match.
  const displayAngleDeg = ray.distance < 0 ? (ray.angleDeg + 180) % 360 : ray.angleDeg;
  const angle = Number.isInteger(displayAngleDeg)
    ? String(displayAngleDeg)
    : displayAngleDeg.toFixed(4);
  return `${dist} < ${angle}°`;
}

/** Format the OTRACK tooltip — one reading per engaged ray (1 = single path, 2 = combined). */
export function formatOtrackTip(rays: readonly OtrackRayHit[]): string {
  return rays.map(formatOtrackRayTip).join("  |  ");
}

/**
 * Aperture in WORLD units, sized like `resolveObjectSnap`'s own aperture
 * conversion (`aperturePx / pixelsPerWorld`) — the caller supplies the
 * already-converted value so this module stays THREE/DOM-free.
 */
export function otrackLockedCursor(
  world: OtrackPoint,
  snapMagnetized: boolean,
  apertureWorld: number,
  referenceAngleDeg: number | null = null,
  /**
   * OTRACK-SUBSYSTEM (local Codex review round 5 P2 — "acquired points need
   * a spaceId") — the CURRENT emit space id (`aidedPointerSnap.ts`'s own
   * `emitSpaceId`). Acquired points are filtered to `spaceId === currentSpaceId`
   * before participating in alignment — a point acquired in a DIFFERENT
   * MSPACE_VIA_VP space (e.g. paper/layout, captured while hovering outside
   * the active viewport) is not a valid coordinate in this space and must
   * never project a bogus ray. `null`/omitted disables the filter (every
   * acquired point participates regardless of space) — kept for call sites
   * that don't distinguish spaces (most unit tests); the production caller
   * (`draftAidConstraint.ts` ← `aidedPointerSnap.ts`) always supplies the
   * real value.
   */
  currentSpaceId: string | null = null,
  /**
   * DOCUMENT frame → the ACTIVE INPUT SPACE — `ActiveInputSpace.fromDocument`
   * of the space `world` is expressed in. Omitted ⇒ the identity, which is
   * what every pre-OTRACK-frame call site (and every unit test that stages
   * the store directly) wants, and is exact whenever the session origin is
   * `(0,0,0)`.
   *
   * ## Why the store is not already in this frame
   *
   * `useOtrackAcquisitionStore` holds DOCUMENT coordinates, because its two
   * writers sit on opposite sides of the pointer pipeline: the hover-dwell
   * acquisition converts an active-space snap hit with `toDocument`, and the
   * `TT` sub-prompt (`scene/otrackTrackPointSession.ts`) receives a document
   * point from the draft state machine and stores it verbatim. The ACTIVE
   * space, by contrast, is NOT frame-uniform — identity ⇒ offset-world,
   * MSPACE_VIA_VP over an unlocked VP ⇒ document — so `spaceId: "model"`
   * alone cannot say which frame a stored point is in. Converting HERE, with
   * the resolver's own branch-aware pair, is what lets one store serve both.
   *
   * `world`, the returned `world`, and every `rays[].acquiredWorld` stay in
   * the ACTIVE space, which is the frame the caller's `guideToOverlay`
   * (`ActiveInputSpace.fromSpace`) expects.
   */
  fromDocument: ((p: OtrackPoint) => OtrackPoint) | null = null,
): OtrackDraftLock {
  const passthrough: OtrackDraftLock = { world, guide: null };
  if (snapMagnetized) return passthrough;
  const sysvar = useSysvarStore.getState();
  if (!selectEffectiveOtrack(sysvar)) return passthrough;

  const allAcquired = useOtrackAcquisitionStore.getState().acquiredPoints;
  const inSpace =
    currentSpaceId == null
      ? allAcquired
      : allAcquired.filter((p) => p.spaceId === currentSpaceId);
  if (inSpace.length === 0) return passthrough;
  // Store (DOCUMENT) → the ACTIVE space `world` lives in. See `fromDocument`.
  const acquired =
    fromDocument === null
      ? inSpace
      : inSpace.map((p) => ({ ...p, world: fromDocument(p.world) }));

  const usePolarAngles = selectOtrackUsesPolarAngles(sysvar);
  const prefs = usePolarTrackingPrefs.getState();
  const polarConfig: PolarTrackingConfig = {
    incrementAngleDeg: sysvar.values.POLARANG,
    additionalAnglesDeg: prefs.additionalAnglesDeg,
    useAdditionalAngles: selectPolarAddAngEnabled(sysvar),
    angleBasis: selectPolarAngleBasis(sysvar),
  };
  const angles = otrackAlignmentAngles(usePolarAngles, polarConfig, referenceAngleDeg ?? 0);

  const result = resolveOtrackAlignment(acquired, world, angles, apertureWorld);
  if (!result.engaged) return passthrough;

  return {
    world: result.world,
    guide: {
      rays: result.rays,
      tip: formatOtrackTip(result.rays),
    },
  };
}
